/**
 * Native playback through the bundled mpv, when running in the desktop shell.
 *
 * This is the whole reason the desktop app exists beyond a browser tab. In a
 * browser the player must hand MKV / HEVC / AC-3 to the server's `/transmux/`
 * endpoint, which runs ffmpeg per viewer. mpv decodes all of it natively, and —
 * because it is not a browser — is not bound by the mixed-content rule, so it
 * can pull the plain-HTTP panel URL directly from an HTTPS page. The desktop
 * app therefore costs this server no ffmpeg and no proxy bandwidth at all.
 *
 * Embedded subtitles come free with it: mpv renders the MKV's own tracks, so
 * the `/transmux/.../subtitle/N.vtt` extraction path is not involved.
 *
 * The browser engines are left completely untouched. When `active` is false
 * every caller falls through to the hls.js / mpegts.js path exactly as before.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { desktopMpv, isDesktop } from "./desktop";

export const NATIVE_FLAG = "brainzr.nativePlayback";

/**
 * Native playback is ON in the desktop shell; the flag is an escape hatch.
 *
 * Setting it to "off" forces the browser engines back without a redeploy,
 * which is what makes A/B comparison possible while the overlay is being
 * debugged on hardware. It is deliberately not off-by-default: hiding a broken
 * path stops it being fixed, and native decode is the entire reason the desktop
 * app exists over a browser tab.
 *
 *   localStorage.setItem("brainzr.nativePlayback", "off")  // then reload
 */
export function nativePlaybackEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(NATIVE_FLAG) !== "off";
  } catch {
    return true;  // storage blocked — the shell check below still gates it
  }
}

export interface MpvEligibility {
  /** The opt-in flag above. */
  enabled: boolean;
  /** Running inside the Electron shell. */
  desktop: boolean;
  /** The shell exposed an mpv bridge. */
  hasMpv: boolean;
  /** Playback is on a Chromecast — the receiver owns it, not us. */
  casting: boolean;
  /** The raw upstream URL. mpv needs the source, not our transmux. */
  nativeUrl: string | null | undefined;
}

/**
 * Should mpv handle this stream?
 *
 * Pure and exported so the matrix is pinned by tests rather than discovered on
 * a Windows box. A false here is always safe: it means the browser engines run,
 * which is the behaviour that already ships.
 */
export function shouldUseMpv(e: MpvEligibility): boolean {
  if (!e.enabled) return false;
  if (!e.desktop || !e.hasMpv) return false;
  if (e.casting) return false;
  const u = (e.nativeUrl ?? "").trim();
  if (!u) return false;
  // Only absolute http(s) sources. A same-origin path would be one of our own
  // proxy/transmux endpoints, which is precisely what this exists to avoid,
  // and mpv has no notion of our origin anyway.
  return /^https?:\/\//i.test(u);
}

export interface MpvPlayback {
  /** True when mpv owns playback and the browser engines must stand down. */
  active: boolean;
  playing: boolean;
  currentTime: number;
  duration: number;
  error: string | null;
  play(): void;
  pause(): void;
  toggle(): void;
  seekTo(seconds: number): void;
  setVolume(level: number): void;
}

export function useMpvPlayback(opts: {
  nativeUrl: string | null | undefined;
  isLive: boolean;
  casting: boolean;
  startAt?: number;
  onProgress?: (seconds: number) => void;
  onEnded?: () => void;
}): MpvPlayback {
  const mpv = desktopMpv();
  const active = useMemo(
    () => shouldUseMpv({
      enabled: nativePlaybackEnabled(),
      desktop: isDesktop(),
      hasMpv: !!mpv,
      casting: opts.casting,
      nativeUrl: opts.nativeUrl,
    }),
    [mpv, opts.casting, opts.nativeUrl],
  );

  // Once mpv has failed for a source we hand playback back to the browser
  // engines rather than leaving a dead player. Without this, standing the
  // browser engine down (which Player does when mpv is active) turns any mpv
  // failure into silence and a black rectangle with no way out.
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Held in refs so the property subscription below never needs to re-run:
  // resubscribing would drop events, and mpv only pushes on change.
  const onProgressRef = useRef(opts.onProgress);
  const onEndedRef = useRef(opts.onEnded);
  onProgressRef.current = opts.onProgress;
  onEndedRef.current = opts.onEnded;

  // Start / restart playback whenever the source changes.
  // A new source deserves a fresh attempt — the latch is per-source.
  useEffect(() => { setFailed(false); }, [opts.nativeUrl]);

  useEffect(() => {
    if (!active || !mpv || !opts.nativeUrl) return;
    let cancelled = false;
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    mpv
      .play({ url: opts.nativeUrl, isLive: opts.isLive, startAt: opts.startAt })
      .then(() => { if (!cancelled) setPlaying(true); })
      .catch((e: unknown) => {
        if (cancelled) return;
        // The most likely first-run failure by far is a missing binary, and
        // "mpv not found" is far more actionable than a raw spawn error.
        setError(e instanceof Error ? e.message : String(e));
        setFailed(true);   // hand this source back to the browser engines
      });
    return () => {
      cancelled = true;
      void mpv.stop();
    };
  }, [active, mpv, opts.nativeUrl, opts.isLive, opts.startAt]);

  // mpv pushes properties rather than being polled.
  useEffect(() => {
    if (!active || !mpv) return;
    return mpv.onProperty((name, value) => {
      switch (name) {
        case "time-pos":
          if (typeof value === "number") {
            setCurrentTime(value);
            onProgressRef.current?.(value);
          }
          break;
        case "duration":
          // Live streams report a growing or absent duration; keeping 0 lets
          // the UI show a live badge rather than a meaningless scrub bar.
          if (typeof value === "number" && Number.isFinite(value)) setDuration(value);
          break;
        case "pause":
          if (typeof value === "boolean") setPlaying(!value);
          break;
        case "eof-reached":
          if (value === true) { setPlaying(false); onEndedRef.current?.(); }
          break;
      }
    });
  }, [active, mpv]);

  return {
    active: active && !failed,
    playing,
    currentTime,
    duration,
    error,
    play: () => { if (mpv) { void mpv.resume(); setPlaying(true); } },
    pause: () => { if (mpv) { void mpv.pause(); setPlaying(false); } },
    toggle: () => {
      if (!mpv) return;
      if (playing) { void mpv.pause(); setPlaying(false); }
      else { void mpv.resume(); setPlaying(true); }
    },
    seekTo: (s: number) => { if (mpv) void mpv.seek(Math.max(0, s)); },
    setVolume: (v: number) => { if (mpv) void mpv.setVolume(Math.max(0, Math.min(1, v))); },
  };
}
